import React, { Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import PropTypes from 'prop-types';
import { map, pick } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import Cookies from 'js-cookie';
import fileDownload from 'js-file-download';
import dayjs from 'dayjs';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import moment from 'moment-timezone';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin

import {
     BADGE_STATUS,
     BROWSER_CLASS,
     BUNDLE_SEAT_STATUS,
     CERTIFICATION_LEVEL,
     COOKIE_NAMES,
     COURSE_STATUS,
     DEFAULT_DATE_FORMAT,
     DEFAULT_DATE_MONTH_NAME_FORMAT,
     DEFAULT_DATE_MONTH_NAME_TIME_FORMAT,
     DEFAULT_DATE_SHORTEN_FORMAT,
     DEFAULT_DATE_SHORTEN_MONTH_NAME_FORMAT,
     DEFAULT_FN,
     DISCOUNT_TYPE,
     ENV_OBJ,
     EVENT_CREDIT_STATUS,
     FALSY_VALUES,
     LOCAL_CART,
     LOCAL_COUPON,
     LOCAL_COUPON_VOUCHERS,
     LOCAL_LOGIN_AS_SESSION,
     LOCAL_LOGIN_AS_UNDER_USER,
     LOCAL_ORG_USER,
     LOCAL_PAYMENT_METHOD,
     LOCAL_TIME_ZONE,
     LOCAL_TYPE_OF_PURCHASE,
     LOCAL_USER_ROLE,
     ORDER_COUNTRY_DEFAULT,
     ORG_USER_ROLES,
     PREREQUISITE_CERTIFICATION_LEVELS,
     PRODUCT_TYPES_REQUIRE_EVENT,
     REGEX_EMAIL,
     SEAT_STATUS,
     SESSION_FORCE_LOGGED_USER_EXIT,
     SESSION_TOTAL_ORDER,
     TIME_ZONE_EST,
     TRAINING_PRODUCT_TYPES,
     TRAINING_TYPES,
} from '../constants';
import countries from '../assets/countries.json';
import states_US_UK from '../assets/states.json';
import stateAllCountries from '../assets/states-all-countries.json';

import { ZipcodeAPI } from '../services/apis/zipcodeAPI';

dayjs.extend(utc);
dayjs.extend(timezone);

const domain = ENV_OBJ[process.env.REACT_APP_ENV] ?? '';

export const clearLocalStorage = () => {
     localStorage.clear();
};

export const clearRelatedLocalCartLocalStorage = () => {
     localStorage.removeItem(LOCAL_CART);
     localStorage.removeItem(LOCAL_COUPON);
     localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
};

export const clearSomeLocalStorage = () => {
     localStorage.removeItem(LOCAL_USER_ROLE);
     localStorage.removeItem(LOCAL_TYPE_OF_PURCHASE);
     localStorage.removeItem(LOCAL_PAYMENT_METHOD);
     localStorage.removeItem(LOCAL_LOGIN_AS_UNDER_USER);
     localStorage.removeItem(LOCAL_LOGIN_AS_SESSION);
     localStorage.removeItem(LOCAL_ORG_USER);
     localStorage.removeItem(LOCAL_TIME_ZONE);
};

export const removeLoggedInCookie = () => {
     const options = {};

     // if (!window.location.hostname.includes('.local')) {
     //      options.domain = domain;
     // }

     Cookies.remove(COOKIE_NAMES.TP_IS_LOGGED_IN, options);
};

export const removeAllCookies = () => {
     Object.keys(Cookies.get()).forEach((cookieName) => {
          const neededAttributes = {
               // Here you pass the same attributes that were used when the cookie was created
               // and are required when removing the cookie
          };
          Cookies.remove(cookieName, neededAttributes);
     });
};

export const removeStorageApplication = () => {
     // Remove cookie
     Cookies.remove(COOKIE_NAMES.CURRENT_USER, { domain });
     Cookies.remove(COOKIE_NAMES.ORGANIZATION_USER, { domain });

     // Remove session storage
     sessionStorage.removeItem(SESSION_FORCE_LOGGED_USER_EXIT);
     sessionStorage.removeItem(SESSION_TOTAL_ORDER);

     clearSomeLocalStorage();
     removeLoggedInCookie();
};

export const isoDateToString = (isoDate) => {
     return new Date(isoDate).toLocaleDateString();
};

// Used to validate between fields of formik validationSchema
export const validateFormik = (getValidationSchema, next = DEFAULT_FN) => {
     const getErrorsFromValidationError = (validationError) => {
          const FIRST_ERROR = 0;
          return validationError.inner.reduce((errors, error) => {
               return {
                    ...errors,
                    [error.path]: error.errors[FIRST_ERROR],
               };
          }, {});
     };

     return (values) => {
          next();
          const validationSchema = getValidationSchema(values);

          try {
               validationSchema.validateSync(values, { abortEarly: false });
               return {};
          } catch (error) {
               return getErrorsFromValidationError(error);
          }
     };
};

export const toastError = (response) => {
     switch (response.status) {
          case 413:
               toast.error('File size must be < 25 MB');
               break;
          case 408:
               toast.error('Request Timeout');
               break;
          case 504:
               toast.error('Gateway Timeout!');
               break;
          default:
               toast.error(response?.data?.message || response?.data?.error || response?.data?.errors?.[0]?.message || response.statusText);
               break;
     }
};

// Get from the character index after first dash to the end of string.
// Example: activity_created_at => created_at or user_name => name
export const getAfterFirstDash = (str) => {
     let fieldName = str;
     const dashIndex = fieldName.indexOf('_');

     if (dashIndex !== -1) {
          return fieldName.substr(dashIndex + 1);
     }

     return fieldName;
};

// Turn thing 1 and 2 into json string and compare
export const jsonAndCompare = (thing1, thing2) => {
     return JSON.stringify(thing1) === JSON.stringify(thing2);
};

export const isEmpty = (array) => {
     var checkEmpty = false;
     array.some((element) => {
          if (element === '') {
               checkEmpty = true;
               return true;
          }
          checkEmpty = false;
          return false;
     });
     return checkEmpty;
};

export const isEmptyObject = (obj) => {
     let hasEmpty = true;

     Object.keys(obj).forEach((key) => {
          if (obj[key] !== '' && obj[key] !== undefined) {
               hasEmpty = false;
          }
     });

     return hasEmpty;
};

export const isDuplicate = (array) => {
     for (var i = 0; i < array.length; i++) {
          for (var j = i + 1; j < array.length; j++) {
               if (array[i] === array[j]) {
                    return true;
               }
          }
     }
     return false;
};

export const checkDomain = (data) => {
     const domainFormat = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
     var checkDomain = false;
     data.some((domain) => {
          if (!domainFormat.test(domain)) {
               checkDomain = true;
               return true;
          }
          checkDomain = false;
          return false;
     });
     return checkDomain;
};

export const useOutsideHandling = (ref, clickOutside) => {
     React.useEffect(() => {
          const handleClickOutside = (event) => {
               if (
                    ref.current &&
                    !ref.current.contains(event.target) &&
                    event.target.className !== 'setting-icon-image' &&
                    event.target.className !== 'form-control member-add-seat'
               ) {
                    clickOutside();
               }
          };

          const handlePressEsc = (event) => {
               if (ref.current && event.key === 'Escape') {
                    clickOutside();
               }
          };

          // Bind the event listener
          document.addEventListener('click', handleClickOutside);
          document.addEventListener('keydown', handlePressEsc);
          document.addEventListener('mousedown', handleClickOutside);

          return () => {
               // Unbind the event listener on clean up
               document.removeEventListener('click', handleClickOutside);
               document.removeEventListener('keydown', handlePressEsc);
               document.removeEventListener('mousedown', handleClickOutside);
          };
     }, [ref, clickOutside]);
};

export const getAnUrlParam = (href) => {
     var vars = {};
     const getParam = (m, key, value) => {
          vars[key.toLowerCase()] = value;
     };
     href.replace(/[?&]+([^=&]+)=([^&]*)/gi, getParam);

     return vars;
};

export const getLocalItem = (itemName) => {
     return JSON.parse(localStorage.getItem(itemName));
};

export const roundTo2DecimalPlaces = (number) => {
     return Math.round(number * 100) / 100;
};

export const numberWithCommas = (number) => {
     return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const tryParseJSON = (jsonString) => {
     try {
          if (!jsonString) return false;

          if (jsonString && typeof jsonString === 'object') {
               return jsonString;
          }

          let o = JSON.parse(jsonString);
          if (o && typeof o === 'object') {
               return o;
          }
     } catch (e) {
          return false;
     }
     return false;
};

export const tryDecodeURIComponent = (encodedURIComponent) => {
     try {
          if (!encodedURIComponent) return '';

          if (encodedURIComponent && typeof encodedURIComponent === 'object') {
               return encodedURIComponent;
          }

          const decode = decodeURIComponent(encodedURIComponent);
          return decode;
     } catch (e) {
          return '';
     }
};

export const timestampId = () => {
     const now = Date.now();
     return now.toString().slice(-8);
};

export const typeOf = (value) => Object.prototype.toString.call(value).slice(8, -1);

export const tagPropType = PropTypes.oneOfType([
     PropTypes.func,
     PropTypes.string,
     PropTypes.shape({ $$typeof: PropTypes.symbol, render: PropTypes.func }),
     PropTypes.arrayOf(
          PropTypes.oneOfType([
               PropTypes.func,
               PropTypes.string,
               PropTypes.shape({
                    $$typeof: PropTypes.symbol,
                    render: PropTypes.func,
               }),
          ])
     ),
]);

export const removeURLParameter = (url, parameter, retainUrlHash = true) => {
     if (!url) {
          return '';
     }

     const urlParts = url.split(/[?|#]/);
     if (urlParts.length < 2 || !parameter) {
          return url;
     }

     // Get first part, and remove from array
     const urlBase = urlParts.shift();

     // Get url hash, and remove from array
     const urlHash = retainUrlHash && urlParts.length > 1 ? `#${urlParts.pop().replace(/\/$/, '')}` : '';

     // Join it back up
     const queryString = urlParts.join('?');

     const prefix = encodeURIComponent(parameter) + '=';
     const parts = queryString.split(/[&;]/g);

     // Reverse iteration as may be destructive
     for (let i = parts.length; i-- > 0; ) {
          if (parts[i].lastIndexOf(prefix, 0) !== -1) {
               parts.splice(i, 1);
          }
     }

     if (!parts.length) {
          if (urlBase.substr(urlBase.length - 1) === '/') {
               return urlBase + urlHash;
          }
          return `${urlBase}${urlHash}/`;
     }

     return urlBase + '?' + parts.join('&') + urlHash;
};

export const getURLParams = (searchString = window.location.search.slice(1)) => {
     const params = {};
     const regex = /([^&=]+)=([^&]*)/g;
     let m = regex.exec(searchString);

     while (m) {
          params[decodeURIComponent(m[1])] = decodeURIComponent(m[2]);
          m = regex.exec(searchString);
     }

     return params;
};

export const getURLParamsPagination = (searchString) => {
     if (!searchString) return {};

     return getURLParams(searchString.replace(/[?/]/g, ''));
};

export const getTimeZoneOffset = (timeZone) => {
     const offsetInMinutes = dayjs().tz(timeZone).$offset; // The time difference between UTC time and "timeZone", in minutes
     const sign = offsetInMinutes > 0 ? '+' : '-';
     let offsetInHours = Math.abs(offsetInMinutes / 60);
     const minutes = (offsetInHours % 1) * 60;
     offsetInHours = Math.trunc(offsetInHours);
     let offsetInHoursStr = offsetInHours.toString();
     const theLast2Digits = minutes === 0 ? '00' : minutes.toString();

     if (offsetInHoursStr.length === 1) {
          offsetInHoursStr = `${sign}0${offsetInHoursStr}:${theLast2Digits}`;
     } else {
          offsetInHoursStr = `${sign}${offsetInHoursStr}:${theLast2Digits}`;
     }

     return `GMT${offsetInHoursStr}`;
};

export const base64Decode = (dataEncode) => {
     return JSON.parse(Buffer.from(dataEncode, 'base64').toString('ascii'));
};

export const base64Encode = (data) => {
     return Buffer.from(JSON.stringify(data)).toString('base64');
};

export const debounce = (fn, delay) => {
     let timeoutId;

     return function (...args) {
          // Cancel previously unexecuted timeouts
          if (timeoutId) {
               clearTimeout(timeoutId);
          }

          timeoutId = setTimeout(() => {
               fn(...args);
          }, delay);
     };
};

export const delay = (delay) => {
     return new Promise((resolve) => {
          setTimeout(() => {
               resolve(true);
          }, delay);
     });
};

export const guidGenerator = () => {
     const S4 = function () {
          return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
     };
     return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
};

export const removeURLParams = () => {
     window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
};

export const capitalizeFirstLetter = (str) => {
     if (!str || typeof str !== 'string') {
          return '';
     }

     str = str.toLowerCase();
     return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getEditorStateFromHTML = (html = '') => {
     if (!html) {
          return '';
     }

     const contentBlock = htmlToDraft(html);
     return EditorState.createWithContent(ContentState.createFromBlockArray(contentBlock));
};

export const getHTMLFromDraft = (e) => {
     return draftToHtml(convertToRaw(e.getCurrentContent()));
};

export const removeNullAttr = (object) => {
     return Object.keys(object)
          .filter((k) => object[k] != null)
          .reduce((a, k) => ({ ...a, [k]: object[k] }), {});
};

export const generateId = () => {
     const now = Date.now();
     const timestampId = now.toString().slice(-8);
     return uuidv4() + '-' + timestampId;
};

export const generaPassword = (passwordLength = 10) => {
     const chars = '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';
     let password = '';

     for (let i = 0; i <= passwordLength; i++) {
          const randomNumber = Math.floor(Math.random() * chars.length);
          password += chars.substring(randomNumber, randomNumber + 1);
     }

     return password;
};

export const limitCharacters = (string = '', limit = 0) => {
     if (string.length > limit) {
          return string.substring(0, limit) + '...';
     }

     return string.substring(0, limit);
};

export const titleCase = (str) => {
     return str.toLowerCase().replace(/(^|\s)\S/g, function (l) {
          return l.toUpperCase();
     });
};

export const removeDefaultLink = (str) => {
     const haveLink = str?.substr(0, 4);
     if (haveLink === 'http') {
          return (str = '');
     } else {
          return str;
     }
};

export const replaceHyphenWithSpace = (str) => {
     if (!str) return '';

     const newStr = str.toString().replace(/-/g, ' ');
     return titleCase(newStr);
};

export const removeTrailingSlash = (str) => {
     if (!str) return '';

     return str.replace(/\/+$/, '');
};

export const convertToSlug = (text) => {
     return text
          .trim()
          .toLowerCase()
          .replace(/ /g, '-')
          .replace(/[^\w-]+/g, '');
};

export const getFileData = (filePath) => {
     if (!filePath) return {};

     const originalFileArr = filePath.split('/');
     const fileName = originalFileArr[originalFileArr.length - 1];
     const fileNameArr = fileName.split('.');
     const extension = fileNameArr[fileNameArr.length - 1];

     return {
          fileName,
          extension,
     };
};

export const showPriceProduct = (listProduct) => {
     if (!listProduct?.length) {
          return 'Contact Us for Pricing';
     }

     const productPrices = listProduct.map((product) => product.price);

     const minPrice = Math.min(...productPrices);
     const maxPrice = Math.max(...productPrices);

     if (minPrice === 0 && maxPrice === 0) {
          return 'Free';
     }

     if (minPrice === 0 && maxPrice > 0) {
          return `$0`;
     }

     if (minPrice === maxPrice) {
          return `$${maxPrice}`;
     }

     if (minPrice > 0 || maxPrice > 0) {
          return `$${minPrice}`;
     }

     return 'Contact Us for Pricing';
};

export const showPriceProductPage = ({ listProduct, isBookGroup, isCertifiedReview, priceOnly = false }) => {
     if (!listProduct?.length) return 'Contact Us for Pricing';

     const sellingGroupMapping = {
          [`${TRAINING_PRODUCT_TYPES.BUNDLE}`]: 'Package',
          [`${TRAINING_PRODUCT_TYPES.CE_CREDITS}`]: 'CE Credits',
          [`${TRAINING_PRODUCT_TYPES.TEST_ONLY}`]: 'CE Credits',
          [`${TRAINING_PRODUCT_TYPES.SURVEY_ONLY}`]: 'CE Credits',
          [`${TRAINING_PRODUCT_TYPES.DIGITAL_DOWNLOAD}`]: 'Digital',
          [`${TRAINING_PRODUCT_TYPES.ONLINE_COURSE}`]: 'Course',
          [`${TRAINING_PRODUCT_TYPES.PURCHASE_DIGITAL_DOWNLOAD}`]: 'eBook',
          [`${TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT}`]: 'Paperback',
          [`${TRAINING_PRODUCT_TYPES.LIVE_TRAINING}`]: 'Training',
     };
     const orderListSellingGroup = ['eBook', 'Paperback', 'Digital', 'Training', 'Course', 'Package', 'CE Credits'];

     //remove product type ce from the listProduct
     if (isBookGroup) {
          listProduct = listProduct.filter(
               (product) => ![TRAINING_PRODUCT_TYPES.CE_CREDITS, TRAINING_PRODUCT_TYPES.TEST_ONLY].includes(product.productType.trainingProductType)
          );
     }

     const newListProduct = listProduct.map((product) => {
          const { trainingProductType } = product.productType;
          const { trainingType } = product;
          let name = sellingGroupMapping[trainingProductType];
          let productPrice = [product.price];

          if (trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING) {
               name = trainingType === TRAINING_TYPES.CALL ? 'Calls' : name;
               const pricingTiers = tryParseJSON(product.pricingTiers);

               if (pricingTiers && trainingType === TRAINING_TYPES.CALL && product.tieredPricingEnable) {
                    const prices = pricingTiers.filter((product) => product.price).map((product) => parseInt(product.price, 10));
                    const maxPrice = prices.length ? Math.max(...prices) : undefined;
                    productPrice = [product.price, maxPrice].filter((p) => p !== undefined);
               }
          }

          return {
               price: productPrice,
               name: name || 'Unknown',
          };
     });

     //sort product by orderList;
     newListProduct.sort((a, b) => {
          const indexA = orderListSellingGroup.indexOf(a.name);
          const indexB = orderListSellingGroup.indexOf(b.name);

          // If the names are not in the orderList, place them at the end
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
     });

     // Group products by name
     const groupedProducts = newListProduct.reduce((acc, product) => {
          const key = product.name;
          if (!acc[key]) {
               acc[key] = { name: key, prices: [], productCount: 0 };
          }

          acc[key].productCount += 1;
          acc[key].prices.push(...product.price);

          return acc;
     }, {});

     Object.entries(groupedProducts).forEach(([key, value]) => {
          // Change Package to Packages for multiple products
          if (key === 'Package' && (value.productCount > 1 || value.prices.length > 1)) {
               groupedProducts['Packages'] = value;
               groupedProducts['Packages'].name = 'Packages';
               delete groupedProducts[key];
          }

          // Change Training to Package for CAMS Certified™ Review
          if (key === 'Training' && isCertifiedReview) {
               groupedProducts['Package'] = value;
               groupedProducts['Package'].name = 'Package';
               delete groupedProducts[key];
          }
     });

     if (priceOnly) {
          const productPrices = [];
          Object.values(groupedProducts).forEach((group) => {
               productPrices.push(...group.prices);
          });

          const minPrice = Math.min(...productPrices);
          const maxPrice = Math.max(...productPrices);

          const priceInfo =
               minPrice === 0
                    ? 'Free'
                    : minPrice < 0
                    ? 'Contact Us for Pricing'
                    : productPrices.length > 1
                    ? `$${minPrice} - $${maxPrice}`
                    : `$${minPrice}`;

          return priceInfo;
     }

     const resultHTML = Object.values(groupedProducts)
          .map((group) => {
               const minPrice = Math.min(...group.prices);
               const maxPrice = Math.max(...group.prices);

               const priceInfo =
                    minPrice === 0
                         ? 'Free'
                         : minPrice < 0
                         ? 'Contact Us for Pricing'
                         : group.prices.length > 1
                         ? `$${minPrice} - $${maxPrice}`
                         : `$${minPrice}`;
               return `<div><span>${group.name}</span><span>${priceInfo}</span></div>`;
          })
          .join('');

     return resultHTML;
};

export const showPrice = (price) => {
     if (price > 0) {
          return `$${numberWithCommas(price)}`;
     } else if (price === 0) {
          return 'Free';
     } else {
          return 'Contact Us for Pricing';
     }
};

export const showDiscount = (discount, type) => {
     if (discount <= 0) {
          return '';
     }

     switch (type) {
          case DISCOUNT_TYPE.PRICE:
               return `$${discount}`;
          case DISCOUNT_TYPE.PERCENT:
               return `${discount}%`;
          default:
               return '';
     }
};

export const getProductPrice = (item) => {
     if (!item) return 0;

     const price = Number(item.price) || 0;
     const discountValue = Number(item.discount) || 0;
     const discountType = item.discountType || DISCOUNT_TYPE.PRICE;

     if (!discountValue) return price;

     if (discountType === DISCOUNT_TYPE.PERCENT) {
          return price - price * (discountValue / 100);
     }

     return price - discountValue;
};

export const calculateDiscountPriceWithCartItems = (cartItems) => {
     if (!cartItems?.length) return [];

     const newCartItems = cartItems.map((cartItem) => {
          const cartItemQuantity = Number(cartItem.quantity) || 1;
          const subTotal = cartItem.price * cartItemQuantity;
          let itemDiscount = 0;

          if (cartItem.discount && cartItem.discount > 0) {
               itemDiscount =
                    cartItem.discountType === DISCOUNT_TYPE.PRICE
                         ? cartItem.discount * cartItemQuantity
                         : ((cartItem.price * cartItem.discount) / 100) * cartItemQuantity;
          }

          if (itemDiscount > subTotal) itemDiscount = subTotal;

          const total = Math.max(subTotal - itemDiscount, 0) || 0;

          return {
               ...cartItem,
               price: Number(cartItem.price) || 0,
               quantity: cartItemQuantity,
               discount: Number(cartItem.discount) || 0,
               total,
          };
     });

     return newCartItems;
};

export const formatEventSingleDateTime = (date, timeZone, hideAbbr = false) => {
     if (!date) return '';

     const timezoneConvert = timeZone || getTimeZoneLocalStorage();
     const timeZoneAbbr = getAbbrTimeZoneEST(date, timezoneConvert);
     const newDay = dayjs(date).tz(timezoneConvert);
     const time = newDay.format(DEFAULT_DATE_MONTH_NAME_TIME_FORMAT);

     return hideAbbr ? `${time}` : `${time} ${timeZoneAbbr}`;
};

export const formatEventSameDate = (start, end, timeZone, hideAbbr = false) => {
     if (!start || !end) return '';

     const timezoneConvert = timeZone || getTimeZoneLocalStorage();

     const newStart = dayjs(start).tz(timezoneConvert);
     const newEnd = dayjs(end).tz(timezoneConvert);
     const isSameDate = newStart.get('date') === newEnd.get('date');

     if (!isSameDate) {
          return formatEventSingleDateTime(start);
     }

     const date = newStart.format(DEFAULT_DATE_SHORTEN_MONTH_NAME_FORMAT);
     const finalFormat = `${date} ${newStart.format('h:mm a')} - ${newEnd.format('h:mm a')}`;
     const timeZoneAbbr = getAbbrTimeZoneEST(start, timezoneConvert);

     return hideAbbr ? `${finalFormat}` : `${finalFormat} ${timeZoneAbbr}`;
};

export const formatEventSingleDate = (day, timeZone) => {
     const newDay = dayjs(day).tz(timeZone);
     return `${newDay.format(DEFAULT_DATE_MONTH_NAME_FORMAT)}`;
};

export const formatEventSingleDateTimeNotConvert = (day, timeZone) => {
     const timezoneConvert = timeZone || getTimeZoneLocalStorage();
     const timeZoneAbbr = getAbbrTimeZoneEST(day, timezoneConvert);
     const newDay = dayjs(day);
     const time = `${newDay.format(DEFAULT_DATE_MONTH_NAME_TIME_FORMAT)}`;

     return time + ' ' + timeZoneAbbr;
};

export const formatEventMultiDate = (startAt, endAt, timeZone) => {
     let time = '';

     const timezoneConvert = timeZone || getTimeZoneLocalStorage();
     const newStartAt = dayjs(startAt).tz(timezoneConvert);
     const newEndAt = dayjs(endAt).tz(timezoneConvert);

     if (newStartAt.format('DD/MM/YYYY') === newEndAt.format('DD/MM/YYYY')) {
          time = `${newStartAt.format('ddd, MMM DD, YYYY')}`;
     } else {
          time = `${newStartAt.format('ddd, MMM DD, YYYY')} - ${newEndAt.format('ddd, MMM DD, YYYY')}`;
     }
     return time;
};

/**
 *
 * @param date Includes data types: string | number | Date | dayjs.Dayjs | undefined | null. If falsy then returns empty string
 * @param timezone Data type: string. Default: 'America/New_York'
 * @param format Data type: string. Default: 'MM-DD-YYYY'
 * @param useTimezone Data type: boolean. Default: true
 * @param useFormat Data type: boolean. Default: true
 * @returns string | dayjs.Dayjs
 */
export const formatDate = ({ date, timezone = TIME_ZONE_EST, format = DEFAULT_DATE_FORMAT, useTimezone = true, useFormat = true }) => {
     if (!date) return '';

     if (useTimezone && useFormat) {
          return dayjs(date).tz(timezone).format(format);
     }

     if (useTimezone && !useFormat) {
          return dayjs(date).tz(timezone);
     }

     if (useFormat) {
          return dayjs(date).format(format);
     }

     return dayjs(date);
};

export const formatSingleDate = (startAt) => {
     const timezoneConvert = getTimeZoneLocalStorage();
     const newStartAt = dayjs(startAt).tz(timezoneConvert);

     return `${newStartAt.format('ddd, MMM DD, YYYY')}`;
};

export const formatEventTime = (startAt, endAt, timeZone) => {
     const timezoneConvert = timeZone || getTimeZoneLocalStorage();
     const newStartAt = dayjs(startAt).tz(timezoneConvert);
     const newEndAt = dayjs(endAt).tz(timezoneConvert);
     const timeZoneAbbr = getAbbrTimeZoneEST(startAt, timezoneConvert);

     const isSameDate = newStartAt.get('date') === newEndAt.get('date');

     const formatDefault = `${newStartAt.format('h:mm a')} ${timeZoneAbbr}`;
     const formatSameDate = `${newStartAt.format('h:mm a')} - ${newEndAt.format('h:mm a')} ${timeZoneAbbr}`;

     return isSameDate ? formatSameDate : formatDefault;
};

export const getMonth = (startAt) => {
     const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

     const newStartAt = dayjs(startAt);
     const month = newStartAt.get('month');
     const year = newStartAt.get('year');

     return `${monthNames[month]} ${year}`;
};

export const formatPriceWithCurrency = (value, currency = '$') => {
     if (!value) return `${currency}0`;

     return currency + value.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
};

export const percentChart = (num) => {
     const intPart = Math.floor(num);
     const decimalPart = +(num - intPart).toFixed(2);
     let firstDecimal = Math.floor(decimalPart * 10) / 10;
     const secondDecimal = Math.floor(decimalPart * 100) % 10;

     if (secondDecimal > 5) {
          firstDecimal += 0.1;
     }
     return intPart + firstDecimal;
};

export const isOrgUser = () => {
     const organizationUser = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
     return !!organizationUser;
};

export const stringArrayEquals = (a, b) => {
     return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
};

export const asyncLocalStorage = {
     setItem: async (key, value) => {
          return Promise.resolve().then(function () {
               localStorage.setItem(key, value);
          });
     },
     getItem: async (key) => {
          return Promise.resolve().then(function () {
               return localStorage.getItem(key);
          });
     },
     removeItem: async (key) => {
          return Promise.resolve().then(function () {
               return localStorage.removeItem(key);
          });
     },
};

export const setLocalStorage = (key, value) => {
     if (!key && !value) {
          return false;
     }
     return localStorage.setItem(key, value);
};

export const validateEmail = (email) => {
     return String(email).toLowerCase().match(REGEX_EMAIL);
};

export const validateFileExtension = (fileName = '', allow) => {
     let checkError = false;
     if (fileName.toLowerCase().match(allow)) {
          checkError = true;
     }
     return checkError;
};

export const showEventName = (products, item, returnOnlyValue) => {
     const returnElement = (eventName) => {
          if (!eventName) return;

          if (returnOnlyValue) return eventName;

          return (
               <>
                    <br />
                    <span className="cart-event-name">
                         <em>{eventName}</em>
                    </span>
               </>
          );
     };

     let eventName;
     let productFound;

     if (Array.isArray(products)) {
          productFound = products?.find((pro) => pro.id === item.productId);
     } else {
          productFound = products;
     }
     const productType = productFound && productFound.productType?.trainingProductType;
     const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;

     if (!isLiveTraining) return;

     //Show with Event Credit
     if (item.isCredit) {
          return returnElement(`${productFound.genericPublicName} - Date to be selected in the future.`);
     }

     //Show with Custom Date
     if (!item.eventId && !item.tierId) {
          return returnElement('Custom Date');
     }

     //Show with Normal Event
     const productEvents = productFound.events;
     let productEvent = productEvents?.find((event) => event.id === item.eventId) || item.event;

     if (productEvent) {
          if (productEvent.startAt === null) {
               eventName = getTimeCustomDate(productEvent.name);
          } else {
               const time = formatEventSingleDateTime(productEvent.startAt);
               eventName = `${productFound.genericPublicName} - ${time}`;
          }

          return returnElement(eventName);
     }

     eventName = productFound.customEventGenericPublicName || 'Custom Date';

     if (!item.tierId) return returnElement(eventName);

     //Show with Tiers
     const pricingTiers = tryParseJSON(productFound.pricingTiers) || [];
     const pricingTier = pricingTiers.find((tier) => tier.id === item.tierId);

     if (pricingTier) {
          eventName = pricingTier.name;
     }

     for (const product of products) {
          if (!product.pricingTiers) continue;

          const pricingTiers = tryParseJSON(product.pricingTiers) || [];
          const pricingTier = pricingTiers.find((tier) => tier.id === item.tierId);

          if (!pricingTier) continue;

          eventName = pricingTier.name;
          break;
     }

     return returnElement(eventName);
};

//get name product by id

export const getNameProductById = (products, productId, publishName) => {
     let findProduct;
     let name;
     if (Array.isArray(products)) {
          findProduct = products?.find((pro) => pro.id === productId);

          if (publishName) {
               name = findProduct?.publishName;
          } else {
               name = findProduct?.name;
          }
     }

     return name;
};

//show list product bundle
export const showListProductBundle = (productId, products, isPublishName, handleBackToSellingGroupDetails) => {
     const findProductBundle = products.find((product) => product.id === productId);

     if (!findProductBundle) {
          return null;
     }
     const listBundleProduct = tryParseJSON(findProductBundle?.listBundleProduct) || [];
     if (findProductBundle.productType.trainingProductType === TRAINING_PRODUCT_TYPES.BUNDLE) {
          const bundleElements =
               listBundleProduct.length &&
               listBundleProduct.map((element, index) => {
                    const listProduct = getNameProductById(products, element.id, isPublishName) + ` (qty ${element.seats})`;

                    if (handleBackToSellingGroupDetails) {
                         return (
                              <Fragment key={index}>
                                   {listProduct && (
                                        <button onClick={(e) => handleBackToSellingGroupDetails(element.id)} className="button-back-cart d-block">
                                             <span className="cart-event-name bundle">
                                                  <em>{listProduct}</em>
                                             </span>
                                        </button>
                                   )}
                              </Fragment>
                         );
                    } else {
                         return (
                              <Fragment key={index}>
                                   {listProduct && (
                                        <span className="cart-event-name bundle">
                                             <em>{listProduct}</em>
                                        </span>
                                   )}
                              </Fragment>
                         );
                    }
               });

          return bundleElements;
     }
};

export const roundingNumber = (num) => {
     return +(Math.round(num + 'e+2') + 'e-2');
};

export const checkProductHasSeat = (listActiveSeatByUser, productId, eventId) => {
     if (!listActiveSeatByUser?.length) return false;

     if (eventId) {
          return listActiveSeatByUser.some((item) => item.productId === productId && item.eventId === eventId);
     }

     return listActiveSeatByUser.some((item) => item.productId === productId);
};

export const checkProductHasReservedSeat = (listActiveSeatByUser, productId, eventId) => {
     if (!listActiveSeatByUser?.length) return false;

     if (eventId) {
          return listActiveSeatByUser.some((item) => item.status !== SEAT_STATUS.FILLED && item.productId === productId && item.eventId === eventId);
     }

     return listActiveSeatByUser.some((item) => item.status !== SEAT_STATUS.FILLED && item.productId === productId);
};

export const checkLicenseeUserAndCart = (cartLocalStorage, licenseeId, products, returnResult = false) => {
     let cartItems = [];
     if (cartLocalStorage) {
          cartItems = cartLocalStorage.cartItems;
          const checkNotLicensee = cartItems?.some((item) => {
               const productCartItem = products?.find((pro) => item.productId === pro.id);
               const productLicenseeId = productCartItem?.licenseeId;
               return licenseeId !== productLicenseeId;
          });

          let isCheck = false;
          if (checkNotLicensee) {
               localStorage.removeItem(LOCAL_CART);
               localStorage.removeItem(LOCAL_COUPON);
               localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
               isCheck = true;
          }

          if (returnResult) {
               return isCheck;
          }
     }
};

export const checkProductTypeUnlimitedPurchase = (products, productBundle) => {
     const product = products.find((item) => item.id === productBundle.id);
     const trainingProductType = product?.productType?.trainingProductType;

     //check product event credit
     const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
     const purchaseEventCredit = isLiveTraining && product.allowPurchaseEventCredit;
     const allowProductType = [TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT, TRAINING_PRODUCT_TYPES.CE_CREDITS].includes(trainingProductType);

     return !allowProductType && !purchaseEventCredit;
};

export const compareArrSame = (a, b) => {
     if (a && b) {
          const isSameArr = (a, b) => a.id === b;

          const onlyInLeft = (left, right, compareFunction) =>
               left.filter((leftValue) => right.some((rightValue) => compareFunction(leftValue, rightValue)));

          const onlyInA = onlyInLeft(a, b, isSameArr);
          const onlyInB = onlyInLeft(b, a, isSameArr);

          const result = [...onlyInA, ...onlyInB];
          return result;
     }
};

export const getStateFromZipcodeUS = async (zipcode) => {
     const response = {
          zip: '',
          city: '',
          state: '',
          stateAbbreviation: '',
          status: 404,
          message: 'Must pass the zipcode as a string.',
     };

     if (!zipcode) return response;

     if (zipcode.length !== 5) {
          response.message = 'Must pass a 5-digit zipcode.';
          return response;
     }

     const zipResponse = await ZipcodeAPI.lookup({ zipcode });
     if (!zipResponse) {
          response.message = 'The zip code you entered is not valid!';
          return response;
     }

     Object.assign(response, zipResponse);
     return response;
};

export const validateZipcodeAndStateUS = (zipcode, state) => {
     if (!zipcode && !state) return true;
     if (!zipcode || !state || typeof zipcode !== 'string' || typeof state !== 'string' || zipcode.length !== 5) return false;

     zipcode = parseInt(zipcode);

     switch (state) {
          case 'AL':
               if (zipcode >= 35000 && zipcode <= 36999) return true;
               break;
          case 'AK':
               if (zipcode >= 99500 && zipcode <= 99999) return true;
               break;
          case 'AS':
               if (zipcode >= 96700 && zipcode <= 96799) return true;
               break;
          case 'AZ':
               if (zipcode >= 85000 && zipcode <= 86599) return true;
               break;
          case 'AR':
               if ((zipcode >= 71600 && zipcode <= 72999) || (zipcode >= 75500 && zipcode <= 75599)) return true;
               break;
          case 'CA':
               if (zipcode >= 90000 && zipcode <= 96699) return true;
               break;
          case 'CO':
               if (zipcode >= 80000 && zipcode <= 81699) return true;
               break;
          case 'CT':
               if (zipcode >= 6000 && zipcode <= 6999) return true;
               break;
          case 'DE':
               if (zipcode >= 19700 && zipcode <= 19999) return true;
               break;
          case 'DC':
               if (zipcode >= 20000 && zipcode <= 20599) return true;
               break;
          case 'FM':
               if (zipcode >= 96900 && zipcode <= 96999) return true;
               break;
          case 'FL':
               if (zipcode >= 32000 && zipcode <= 34999) return true;
               break;
          case 'GA':
               if ((zipcode >= 30000 && zipcode <= 31999) || (zipcode >= 39800 && zipcode <= 39999)) return true;
               break;
          case 'GU':
               if (zipcode >= 96900 && zipcode <= 96999) return true;
               break;
          case 'HI':
               if (zipcode >= 96700 && zipcode <= 96899) return true;
               break;
          case 'ID':
               if (zipcode >= 83200 && zipcode <= 83899) return true;
               break;
          case 'IL':
               if (zipcode >= 60000 && zipcode <= 62999) return true;
               break;
          case 'IN':
               if (zipcode >= 46000 && zipcode <= 47999) return true;
               break;
          case 'IA':
               if (zipcode >= 50000 && zipcode <= 52899) return true;
               break;
          case 'KS':
               if (zipcode >= 66000 && zipcode <= 67999) return true;
               break;
          case 'KY':
               if (zipcode >= 40000 && zipcode <= 42799) return true;
               break;
          case 'LA':
               if (zipcode >= 70000 && zipcode <= 71499) return true;
               break;
          case 'ME':
               if (zipcode >= 3900 && zipcode <= 4999) return true;
               break;
          case 'MH':
               if (zipcode >= 96900 && zipcode <= 96999) return true;
               break;
          case 'MD':
               if (zipcode >= 20600 && zipcode <= 21999) return true;
               break;
          case 'MA':
               if ((zipcode >= 1000 && zipcode <= 2799) || (zipcode >= 5500 && zipcode <= 5599)) return true;
               break;
          case 'MI':
               if (zipcode >= 48000 && zipcode <= 49999) return true;
               break;
          case 'MN':
               if (zipcode >= 55000 && zipcode <= 56799) return true;
               break;
          case 'MS':
               if (zipcode >= 38600 && zipcode <= 39799) return true;
               break;
          case 'MO':
               if (zipcode >= 63000 && zipcode <= 65899) return true;
               break;
          case 'MT':
               if (zipcode >= 59000 && zipcode <= 59999) return true;
               break;
          case 'NE':
               if (zipcode >= 68000 && zipcode <= 69399) return true;
               break;
          case 'NV':
               if (zipcode >= 88900 && zipcode <= 89899) return true;
               break;
          case 'NH':
               if (zipcode >= 3000 && zipcode <= 3999) return true;
               break;
          case 'NJ':
               if (zipcode >= 7000 && zipcode <= 8999) return true;
               break;
          case 'NM':
               if (zipcode >= 87000 && zipcode <= 88499) return true;
               break;
          case 'NY':
               if ((zipcode >= 500 && zipcode <= 599) || (zipcode >= 6300 && zipcode <= 6399) || (zipcode >= 9000 && zipcode <= 14999)) return true;
               break;
          case 'NC':
               if (zipcode >= 26900 && zipcode <= 28999) return true;
               break;
          case 'ND':
               if (zipcode >= 58000 && zipcode <= 58899) return true;
               break;
          case 'MP':
               if (zipcode >= 96900 && zipcode <= 96999) return true;
               break;
          case 'OH':
               if (zipcode >= 43000 && zipcode <= 45999) return true;
               break;
          case 'OK':
               if (zipcode >= 73000 && zipcode <= 74999) return true;
               break;
          case 'OR':
               if (zipcode >= 97000 && zipcode <= 97999) return true;
               break;
          case 'PW':
               if (zipcode >= 96900 && zipcode <= 96999) return true;
               break;
          case 'PA':
               if (zipcode >= 15000 && zipcode <= 19699) return true;
               break;
          case 'PR':
               if (zipcode >= 600 && zipcode <= 999) return true;
               break;
          case 'RI':
               if (zipcode >= 2800 && zipcode <= 2999) return true;
               break;
          case 'SC':
               if (zipcode >= 29000 && zipcode <= 29999) return true;
               break;
          case 'SD':
               if (zipcode >= 57000 && zipcode <= 57799) return true;
               break;
          case 'TN':
               if (zipcode >= 37000 && zipcode <= 38599) return true;
               break;
          case 'TX':
               if ((zipcode >= 75000 && zipcode <= 79999) || (zipcode >= 88500 && zipcode <= 88599)) return true;
               break;
          case 'UT':
               if (zipcode >= 84000 && zipcode <= 84799) return true;
               break;
          case 'VT':
               if (zipcode >= 5000 && zipcode <= 5999) return true;
               break;
          case 'VA':
               if ((zipcode >= 20100 && zipcode <= 20199) || (zipcode >= 22000 && zipcode <= 24699)) return true;
               break;
          case 'VI':
               if (zipcode >= 800 && zipcode <= 899) return true;
               break;
          case 'WA':
               if (zipcode >= 98000 && zipcode <= 99499) return true;
               break;
          case 'WI':
               if (zipcode >= 53000 && zipcode <= 54999) return true;
               break;
          case 'WV':
               if (zipcode >= 24700 && zipcode <= 26899) return true;
               break;
          case 'WY':
               if (zipcode >= 82000 && zipcode <= 83199) return true;
               break;
          case 'AA':
               if (zipcode >= 34000 && zipcode <= 34099) return true;
               break;
          case 'AE':
               if (zipcode >= 9000 && zipcode <= 9899) return true;
               break;
          case 'AP':
               if (zipcode >= 96200 && zipcode <= 96699) return true;
               break;
          default:
               break;
     }
     return false;
};

export const compareDateExpired = (date) => {
     const toDay = dayjs();
     const dateCompare = dayjs(date);
     return toDay > dateCompare;
};

export const getTimeZoneGuess = () => {
     return moment.tz.guess(true);
};

export const getAbbrTimeZone = (timezone = getTimeZoneGuess()) => {
     return moment.tz(timezone).format('Z');
};

export const getAbbrTimeZoneEST = (date, timezone = getTimeZoneGuess()) => {
     if (!date) {
          date = new Date();
     }

     return moment.tz(date, timezone).format('zz');
};

export const getLabelTimeZone = (timezone = getTimeZoneGuess()) => {
     const offset = moment.tz(timezone).utcOffset();
     const timezoneFormat = offset ? getAbbrTimeZone(timezone) : '';
     const timezoneValue = `(GMT${timezoneFormat}) ${timezone}`;
     return timezoneValue;
};

export const findZoneAbbrEST = (date) => {
     if (!date) {
          date = new Date();
     }

     return moment.tz(date, TIME_ZONE_EST).format('zz');
};

export const getTimezoneESTandEDT = (date) => {
     if (!date) {
          date = new Date();
     }

     const offset = moment.tz(TIME_ZONE_EST).utcOffset(date);
     const abbrTimezone = moment.tz(date, TIME_ZONE_EST).format('Z');
     const timezoneFormat = offset ? abbrTimezone : '';
     const zoneAbbr = findZoneAbbrEST(date);
     const timezoneValue = `(GMT${timezoneFormat}) ${TIME_ZONE_EST} ${zoneAbbr}`;
     return timezoneValue;
};

export const getListTimeZonesMoment = (country) => {
     const listNames = country ? moment.tz.zonesForCountry(country) : moment.tz.names();

     const listTimezones = listNames
          .reduce((nameAccumulator, currentName) => {
               const offset = moment.tz(currentName).utcOffset();
               const timezone = offset ? getAbbrTimeZone(currentName) : '';

               nameAccumulator.push({
                    id: currentName,
                    name: `(GMT${timezone}) ${currentName}`,
                    value: currentName,
                    label: `(GMT${timezone}) ${currentName}`,
                    offset,
               });

               return nameAccumulator;
          }, [])
          .sort((a, b) => a.offset - b.offset);

     return listTimezones;
};

export const getCountryByTimeZone = (timezone) => {
     const countries = moment.tz.zone(timezone).countries() || [];

     return countries[0];
};

export const getTimeZoneLocalStorage = () => {
     const userTimezone = localStorage.getItem(LOCAL_TIME_ZONE);

     if (FALSY_VALUES.includes(userTimezone)) return getTimeZoneGuess();

     return userTimezone || getTimeZoneGuess();
};

export const convertTimeZone = (date, timezone) => {
     const newDate = dayjs(date).format('YYYY-MM-DDTHH:mm:ss.SSS');
     return newDate + moment.tz(newDate, timezone).format('Z');
};

export const changeTimeZone = (date, timeZone, locale = 'en-US') => {
     if (!date) {
          return new Date();
     }

     if (!timeZone) {
          return new Date(date);
     }

     if (typeof date === 'string') {
          return new Date(
               new Date(date).toLocaleString(locale, {
                    timeZone,
               })
          );
     }

     return new Date(
          date.toLocaleString(locale, {
               timeZone,
          })
     );
};

export const convertTimeEST = (currentTime = new Date(), setMinutes = false, setEndAt = false) => {
     const newDate = changeTimeZone(new Date(currentTime), TIME_ZONE_EST);

     if (setMinutes) {
          if (newDate.getMinutes() >= 0 && newDate.getMinutes() <= 29) {
               newDate.setMinutes(30);
          } else {
               newDate.setMinutes(60);
          }
     }

     if (setEndAt) {
          newDate.setMinutes(newDate.getMinutes() + 1);
     }

     return newDate;
};

export const convertSetDateTimePicker = (currentTime = new Date(), setMinutes = false, setEndAt = false) => {
     const newDate = new Date(currentTime);

     if (setMinutes) {
          if (newDate.getMinutes() >= 0 && newDate.getMinutes() <= 29) {
               newDate.setMinutes(30);
          } else {
               newDate.setMinutes(60);
          }
     }

     if (setEndAt) {
          newDate.setMinutes(newDate.getMinutes() + 1);
     }

     return newDate;
};

export const convertConstantToCapitalizeFirstLetter = (str) => {
     if (!str || typeof str !== 'string') {
          return '';
     }

     return str
          .split('_')
          .map((strItem) => {
               const strLowerCase = strItem.toLowerCase();
               return strLowerCase.charAt(0).toUpperCase() + strLowerCase.slice(1);
          })
          .join(' ');
};

export const couponCodeMessagePossible = (str, timezone) => {
     if (!str) return '';

     if (!timezone) {
          timezone = TIME_ZONE_EST;
     }

     const messageMatch = str.match(/\{(.+?)\}/);
     if (!messageMatch) {
          console.log(str);
          return str;
     }

     const startDate = dayjs(messageMatch[1]).tz(timezone).format(DEFAULT_DATE_SHORTEN_FORMAT);
     const message = str.replace(messageMatch[0], startDate);

     console.log(message);
     return message;
};

export const getBadge = (status) => {
     const statusObj = {
          [BADGE_STATUS.DISABLED]: 'secondary',
          [BADGE_STATUS.ERROR]: 'danger',
          [BADGE_STATUS.SHIPPED]: 'success',
          [BADGE_STATUS.ENABLE]: 'primary',
     };

     return statusObj[status] ?? 'primary';
};

export const validURL = (str) => {
     const pattern = new RegExp(
          '^(https?:\\/\\/)?' + // protocol
               '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
               '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
               '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
               '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
               '(\\#[-a-z\\d_]*)?$',
          'i'
     ); // fragment locator
     return !!pattern.test(str);
};

export const getHostnameFromRegex = (url) => {
     // run against regex
     const pattern = new RegExp(
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
               '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
               '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
               '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
               '(\\#[-a-z\\d_]*)?$',
          'i'
     ); // fragment locator

     const matches = url.match(pattern);
     if (!matches) return url;
     // extract hostname (will be null if no match is found)
     return matches && matches[1];
};

export const withParams = (Component) => {
     function ComponentWithRouterProp(props) {
          const params = useParams();
          return <Component {...props} params={params} />;
     }
     return ComponentWithRouterProp;
};

export const addTimezoneLocal = (userTimezone) => {
     const timeZoneLocal = localStorage.getItem(LOCAL_TIME_ZONE);

     if (!timeZoneLocal || timeZoneLocal !== userTimezone) {
          const timeZone = !FALSY_VALUES.includes(userTimezone) ? userTimezone : getTimeZoneGuess();

          localStorage.setItem(LOCAL_TIME_ZONE, timeZone);
     }
};

export const getTimeCustomDate = (customDateName, timeZone) => {
     let splitEventName = [];

     const oldNameFormat = customDateName.includes('-');
     const newNameFormat = customDateName.includes(' | ');

     if (oldNameFormat && !newNameFormat) {
          splitEventName = customDateName.split('-');
          const newName = splitEventName[0].trim();
          return newName;
     }

     return customDateName;
};

export const joinArrayToString = (array = []) => {
     return array.join(', ');
};

export const findCountryLabel = (country = []) => {
     //array country value
     return countries.filter((ct) => country.some((item) => item === ct.value)).map((item) => item.label);
};

export const getCountriesList = (licensees) => {
     if (licensees && licensees.length > 0) {
          const countries = [];
          licensees.forEach((item) => {
               const names = JSON.parse(item.country);
               names.forEach((name) => {
                    countries.push({
                         value: name,
                         label: getCountryLabelByCountry(name),
                         licenseeId: item.id,
                    });
               });
          });
          countries.sort((a, b) => a.label.localeCompare(b.label));
          return sortByOrderListCountryDefault(countries);
     } else {
          return [
               {
                    value: '',
                    label: '',
                    licenseeId: '',
               },
          ];
     }
};

export const getCountries = (licensees) => {
     if (licensees && licensees.length > 0) {
          const countries = [];
          licensees.forEach((item) => {
               const names = JSON.parse(item.country);
               names.forEach((name) => {
                    countries.push({
                         id: name,
                         name: getCountryLabelByCountry(name),
                    });
               });
          });
          countries.sort((a, b) => a.name.localeCompare(b.name));
          return countries;
     } else {
          return [
               {
                    id: '',
                    country: '',
               },
          ];
     }
};

export const findLicenseeIdByCountryValue = (countryValue = '', listLicensees = []) => {
     if (!countryValue) return;

     const licensee = listLicensees?.find((item) => item.country?.includes(countryValue) || item.label?.includes(countryValue));

     if (licensee) {
          if (licensee.value) {
               return licensee.value;
          } else {
               return licensee.id;
          }
     }
     return licensee;
};

export const findLicenseeByCountryValue = (countryValue = '', listLicensees = []) => {
     const licensee = listLicensees.find((item) => item.country?.includes(countryValue) || item.label?.includes(countryValue));
     return licensee;
};

export const getListOptionsCountryByCountry = (countryValue, listLicensees) => {
     if (!countryValue || !listLicensees || listLicensees.length <= 0) {
          return [];
     }
     const licensee = findLicenseeByCountryValue(countryValue, listLicensees);
     let countryLicensee = licensee?.country;
     countryLicensee = JSON.parse(countryLicensee);

     const res = countryLicensee.map((item) => ({
          id: item,
          name: getCountryLabelByCountry(item),
     }));

     return sortByOrderListCountryDefault(res);
};

export const getCountryLabelByCountry = (countryValue = '') => {
     if (countryValue === 'USA') {
          countryValue = 'US';
     }
     if (countryValue === 'UK') {
          countryValue = 'GB';
     }
     return countries.find((item) => item.value === countryValue)?.label || '';
};

export const getCountryValueByCountry = (countryValue = '') => {
     if (countryValue === 'USA') {
          countryValue = 'US';
     }
     if (countryValue === 'UK') {
          countryValue = 'GB';
     }
     return countries.find((item) => item.value === countryValue)?.value || '';
};

export const getStatesByCountryValue = (countryValue = '') => {
     const listState = [];
     stateAllCountries.forEach((state) => {
          if (state.country_code === countryValue) {
               listState.push({
                    value: state.state_code,
                    label: state.name,
               });
          }
     });
     return listState;
};

export const getStatesOptionsByCountryValue = (countryValue = '') => {
     const listState = [];
     stateAllCountries.forEach((state) => {
          if (state.country_code === countryValue) {
               listState.push({
                    id: state.state_code,
                    name: state.name,
               });
          }
     });
     return listState;
};

export const getValueStateByCountry = (countryValue = '', stateValue = '') => {
     let stateLabel = '';
     if (countryValue === 'US') {
          stateLabel = states_US_UK['US'].find((item) => item.value === stateValue)?.label || '';
     } else {
          stateLabel = stateAllCountries.find((item) => item.country_code === countryValue && item.state_code === stateValue)?.name || '';
     }
     return stateLabel;
};

export const getSeatStatus = (status) => {
     if (!status) return '';

     let nameStatus = '';

     switch (status) {
          case SEAT_STATUS.NOT_STARTED:
          case SEAT_STATUS.FILLED:
               nameStatus = 'Not Started';
               break;
          case SEAT_STATUS.IN_PROGRESS:
               nameStatus = 'In Process';
               break;
          case SEAT_STATUS.ACCESS_REMOVED:
               nameStatus = 'Access Removed';
               break;
          default:
               nameStatus = status;
               break;
     }

     return capitalizeFirstLetter(nameStatus);
};

export const getEventCreditStatus = (status) => {
     if (!status) {
          return '';
     }

     let nameStatus = '';

     switch (status) {
          case EVENT_CREDIT_STATUS.OPEN:
               nameStatus = 'Open';
               break;
          case EVENT_CREDIT_STATUS.NO_SELECTED:
               nameStatus = 'DATE NOT SELECTED';
               break;
          case EVENT_CREDIT_STATUS.SELECTED:
               nameStatus = 'DATE SELECTED';
               break;
          case SEAT_STATUS.ACCESS_REMOVED:
               nameStatus = 'Access Removed';
               break;
          default:
               nameStatus = status;
               break;
     }

     return capitalizeFirstLetter(nameStatus);
};

export const getBundleSeatStatus = (status) => {
     if (!status) {
          return '';
     }

     let nameStatus = '';

     switch (status) {
          case BUNDLE_SEAT_STATUS.ACCEPTED:
               nameStatus = 'accepted';
               break;
          default:
               nameStatus = status;
               break;
     }

     return capitalizeFirstLetter(nameStatus);
};

export const cloneDeep = (obj, hash = new WeakMap()) => {
     if (obj === null) {
          return obj;
     }

     if (obj instanceof Date) {
          return new Date(obj);
     }

     if (obj instanceof RegExp) {
          return new RegExp(obj);
     }

     if (typeof obj !== 'object') {
          return obj;
     }

     if (hash.get(obj)) {
          return hash.get(obj);
     }

     const cloneObj = new obj.constructor();

     hash.set(obj, cloneObj);
     for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
               cloneObj[key] = cloneDeep(obj[key], hash);
          }
     }

     return cloneObj;
};

/**
 *
 * @param array Any array
 * @param prop Property to sort. Example:: level 1: 'order'; level 2: 'lessons.order'
 * @param type Sorting type: 'asc' | 'desc'. Default value: 'asc'
 * @returns Array after sorted
 */
export const sortArrayByProperty = (array, prop, type = 'asc', isSortEmpty = true) => {
     const props = prop.split('.');
     const propLength = props.length;

     if (!array?.length) {
          return array;
     }

     array.sort((firstItem, secondItem) => {
          let i = 0;

          while (i < propLength) {
               firstItem = typeof firstItem[props[i]] === 'string' ? firstItem[props[i]].toUpperCase() : firstItem[props[i]];
               secondItem = typeof secondItem[props[i]] === 'string' ? secondItem[props[i]].toUpperCase() : secondItem[props[i]];
               i++;
          }

          if (isSortEmpty && firstItem === null) {
               return type === 'desc' ? -1 : 1;
          }

          if (isSortEmpty && secondItem === null) {
               return type === 'desc' ? 1 : -1;
          }

          if (firstItem < secondItem) {
               return type === 'desc' ? 1 : -1;
          }

          if (firstItem > secondItem) {
               return type === 'desc' ? -1 : 1;
          }

          return 0;
     });

     return array;
};

export const sortByOrderListCountryDefault = (countries) => {
     const orderCountryDefault = [...ORDER_COUNTRY_DEFAULT];
     const orderCountries = [];
     let newCountries = [...countries];

     for (const element of orderCountryDefault) {
          const newArr = newCountries.filter((item) => (item.id ?? item.value).toLowerCase().includes(element.value.toLowerCase()));
          const newArr1 = newCountries.filter((item) => !(item.id ?? item.value).toLowerCase().includes(element.value.toLowerCase()));
          if (newArr.length > 0) {
               for (const a of newArr) {
                    orderCountries.push(a);
               }
          }
          if (newArr1.length > 0) {
               newCountries = [...newArr1];
          }
     }
     return [...orderCountries, ...newCountries];
};

export const scrollToTopOffset = (topOffset = 0) => {
     window.scrollTo({
          top: topOffset,
          behavior: 'smooth',
     });
};

export const parseJwt = (token) => {
     const base64Url = token.split('.')[1];
     const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
     const jsonPayload = decodeURIComponent(
          atob(base64)
               .split('')
               .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
               })
               .join('')
     );

     return JSON.parse(jsonPayload);
};

export const sortOrgMemberRole = (orgMember) => {
     if (orgMember !== undefined) {
          const owner = orgMember.filter((item) => item.orgRole === ORG_USER_ROLES.OWNER);
          const manager = orgMember.filter((item) => item.orgRole === ORG_USER_ROLES.MANAGER);
          const member = orgMember.filter((item) => item.orgRole === ORG_USER_ROLES.USER);
          const result = owner.concat(manager, member);
          return result;
     }
};

export const exportToCSV = (filename, rows) => {
     const processRow = (row) => {
          let finalVal = '';

          for (let i = 0; i < row.length; i++) {
               let innerValue = typeof row[i] === 'string' ? row[i].toString() : '';

               if (row[i] instanceof Date) {
                    innerValue = row[i].toLocaleString();
               }

               let result = innerValue.replace(/"/g, '""');

               if (result.search(/("|,|\n)/g) >= 0) {
                    result = '"' + result + '"';
               }

               if (i > 0) {
                    finalVal += ',';
               }

               finalVal += result;
          }

          return finalVal + '\n';
     };

     let csvFile = '';

     for (let i = 0; i < rows.length; i++) {
          csvFile += processRow(rows[i]);
     }

     // https://stackoverflow.com/questions/17879198/adding-utf-8-bom-to-string-blob
     // Prepend \ufeff to the csvFile to prevent special characters from being updated to other characters
     const blob = new Blob(['\ufeff' + csvFile], { type: 'text/csv;charset=utf-8;' });

     if (navigator.msSaveBlob) {
          navigator.msSaveBlob(blob, filename);
          return;
     }

     const link = document.createElement('a');

     if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);

          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
     }
};

export const removeSpecialCharacters = (str) => {
     if (str === undefined || typeof str !== 'string') {
          return false;
     }
     return str.replace(/[^a-zA-Z ]/g, '');
};

export const sortArrayObjectsAlphabetically = (array, nameSort) => {
     const SortArray = (x, y) => {
          if (x < y) {
               return -1;
          }
          if (x > y) {
               return 1;
          }
          return 0;
     };
     const res = array?.sort((x, y) => SortArray(x[nameSort], y[nameSort])) || [];
     return sortByOrderListCountryDefault(res);
};

export const getHashElement = () => {
     const elementId = window.location.hash.slice(1);
     const elementIdHasEndSlash = elementId[elementId.length - 1];
     if (elementIdHasEndSlash) {
          return elementId.replace('/', '');
     }
     return elementId;
};

export const getLocationLinkEvent = (locationEvent) => {
     let location = locationEvent;

     if (validURL(locationEvent) && locationEvent.slice(0, 4) !== 'http') {
          location = '//' + locationEvent;
     }

     return location;
};

// Same as the server
export const getFirstSellingGroupProductFromProduct = (sellingGroupProducts) => {
     if (!sellingGroupProducts?.length) return undefined;

     let sellingGroupProduct = undefined;

     for (let i = 0; i < sellingGroupProducts.length; i++) {
          const _sellingGroupProduct = sellingGroupProducts[i];

          const _sellingGroup = _sellingGroupProduct.sellingGroup;
          if (!_sellingGroup) continue;

          if (_sellingGroupProduct.sellingGroupPackage && !sellingGroupProduct) {
               sellingGroupProduct = _sellingGroupProduct;
          }

          if (!_sellingGroupProduct.sellingGroupPackage) {
               sellingGroupProduct = _sellingGroupProduct;
               break;
          }
     }

     return sellingGroupProduct;
};

export const getBrowser = () => {
     // Opera 8.0+
     // const isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;

     // Firefox 1.0+
     const isFirefox = typeof InstallTrigger !== 'undefined';

     // Safari 3.0+ "[object HTMLElementConstructor]"
     const isSafari =
          /constructor/i.test(window.HTMLElement) ||
          (function (p) {
               return p.toString() === '[object SafariRemoteNotification]';
          })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));

     // Internet Explorer 6-11
     const isIE = /*@cc_on!@*/ false || !!document.documentMode;

     // Edge 20+
     const isEdge = !isIE && !!window.StyleMedia;

     // Chrome 1 - 79
     const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);

     // Edge (based on chromium) detection
     const isEdgeChromium = isChrome && navigator.userAgent.indexOf('Edg') !== -1;

     // Blink engine detection
     // const isBlink = (isChrome || isOpera) && !!window.CSS;

     //Ipad and iphone safari
     var ua = window.navigator.userAgent;
     var iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
     var webkit = !!ua.match(/WebKit/i);
     var iOSSafari = iOS && webkit && !ua.match(/CriOS/i);

     if (iOSSafari) {
          return BROWSER_CLASS.IS_IOS_SAFARI;
     }

     switch (true) {
          case isFirefox:
               return BROWSER_CLASS.IS_FIREFOX;
          case isSafari:
               return BROWSER_CLASS.IS_SAFARI;
          case isIE:
               return BROWSER_CLASS.IS_IE;
          case isEdge:
               return BROWSER_CLASS.IS_EDGE;
          case isChrome:
               return BROWSER_CLASS.IS_CHROME;
          case isEdgeChromium:
               return BROWSER_CLASS.IS_EDGE_CHROMIUM;
          default:
               return '';
     }
};

export const checkCeProductIsRPT = (product, products) => {
     if (!product) {
          return;
     }

     const listProductPrerequisites = product.productPrerequisites || [];

     const checkRelatedOfRPT = products.some(
          (productItem) =>
               productItem.relatedProductProductId === product.id &&
               PRODUCT_TYPES_REQUIRE_EVENT.includes(productItem.productType?.trainingProductType)
     );

     const checkHasPrerequisites = listProductPrerequisites.some((prod) => {
          const prodFound = products.find((item) => item.id === prod.prerequisiteId) && !prod.certificationLevel;
          return prodFound && PRODUCT_TYPES_REQUIRE_EVENT.includes(prodFound.productType?.trainingProductType);
     });

     return (
          (checkRelatedOfRPT || checkHasPrerequisites) &&
          (product?.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.TEST_ONLY ||
               product?.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.CE_CREDITS)
     );
};

export const downloadFileFromUrl = async (url, fileName) => {
     if (!url) {
          toast.error('Url is required.');
          return;
     }

     if (!fileName) {
          fileName = `download-file`;

          const extension = getFileData(url).extension;

          if (extension) {
               fileName += `.${extension}`;
          }
     }

     return await axios
          .get(url, {
               responseType: 'blob',
          })
          .then((res) => {
               fileDownload(res.data, fileName);
          })
          .catch((err) => {
               toast.error(err);
          });
};

export const setCountDownTime = (date) => {
     if (!date) return null;

     const today = dayjs();
     let countDownTime;

     if (date.diff(today, 'day') > 0) {
          countDownTime = Number(date.diff(today, 'day')) + 1 + ' days';
     } else {
          countDownTime = Number(date.diff(today, 'hour')) + 1 + ' hours';
     }

     if (date.diff(today, 'hour') === 0) {
          countDownTime = Number(date.diff(today, 'minute')) + 1 + ' minutes';
     }

     if (date.diff(today, 'millisecond') < 0) {
          countDownTime = null;
     }

     return countDownTime;
};

export const tooltipCouponContent = (value) => {
     if (!value) {
          return '';
     }

     return value.join('<br>');
};

export const initializeGTM = () => {
     if (!document.querySelector('script[src*="https://www.googletagmanager.com/gtm.js"]')) {
          (function (w, d, s, l, i) {
               w[l] = w[l] || [];
               w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
               var f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s),
                    dl = l !== 'dataLayer' ? '&l=' + l : '';
               j.async = true;
               j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
               f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-T98MQQV');
     }
};

export const removeCommas = (str, comma = ',') => {
     if (!str) {
          return '';
     }

     if (str.startsWith(comma) && str.endsWith(comma)) {
          return str.slice(1, -1);
     }

     if (str.startsWith(comma)) {
          return str.slice(1);
     }

     if (str.endsWith(comma)) {
          return str.slice(0, -1);
     }

     return str;
};

export const mappingCurrentUser = (user) => {
     if (!user || !Object.keys(user).length) return null;

     return {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          country: user.country,
          timezone: user.timezone,
     };
};

export const mappingCurrentOrganizationUser = (organizationUser) => {
     if (!organizationUser || !Object.keys(organizationUser).length) return null;

     const user = organizationUser.user;
     const organization = organizationUser.organization;

     return {
          userId: organizationUser.userId,
          organizationId: organizationUser.organizationId,
          role: organizationUser.role,
          user: {
               id: user.id,
               firstName: user.firstName,
               lastName: user.lastName,
               email: user.email,
          },
          organization: {
               id: organization.id,
               name: organization.name,
               country: organization.country,
               timezone: organization.timezone,
          },
     };
};

/**
 *
 * @param obj Single level object
 * @param keys Array of keys to trim
 * @returns Objects after trim
 */
export const trimAllValuesByKeys = (obj, keys = []) => {
     const objKeys = obj ? Object.keys(obj) : [];

     if (!objKeys.length) return {};

     if (!keys?.length) return obj;

     const newObj = cloneDeep(obj);

     for (let i = 0; i < keys.length; i++) {
          const key = keys[i];

          if (typeof newObj[key] === 'string') {
               newObj[key] = newObj[key].trim();
          }
     }

     return newObj;
};

/**
 *
 * @param filter Object key: column name, value: value filter of column
 * @returns String filter pagination after encode
 */
export const filterQueryPagination = (filter) => {
     if (!filter || !Object.keys(filter).length) return '';

     return `&filter=${encodeURIComponent(JSON.stringify(filter))}`;
};

/**
 *
 * @param path Navigator path
 * @param pagination Pagination object
 * @param navigate Method navigate(useNavigate) form library 'react-router-dom'
 * @returns Navigate to new url
 */
export const navigateURLByPagination = (path, pagination, navigate) => {
     navigate(
          path.replace(
               ':filter',
               `?page=${pagination.page}&pageSize=${pagination.pageSize}&sortBy=${pagination.sortBy}&sortType=${
                    pagination.sortType
               }${filterQueryPagination(pagination.filter)}/`
          )
     );
};

/**
 *
 * @param obj Single level object
 * @returns Objects after trim
 */
export const trimAllObjectValues = (obj) => {
     const objKeys = Object.keys(obj);

     if (!objKeys.length) {
          return {};
     }

     return Object.keys(obj).reduce((acc, curr) => {
          acc[curr] = typeof obj[curr] === 'string' ? obj[curr].trim() : obj[curr];
          return acc;
     }, {});
};

/**
 *
 * @param obj Single level object
 * @returns Objects after trim
 */
export const removeObjectValues = (obj) => {
     const objKeys = Object.keys(obj);

     if (!objKeys.length) {
          return {};
     }

     return Object.keys(obj).reduce((acc, curr) => {
          if (typeof obj[curr] === 'string' && obj[curr].trim() !== '') {
               acc[curr] = obj[curr];
          }

          if (typeof obj[curr] === 'boolean') {
               acc[curr] = obj[curr];
          }

          if (typeof obj[curr] === 'object' && Object.keys(obj[curr]).length) {
               acc[curr] = obj[curr];
          }

          //Object is Date
          if (typeof obj[curr] === 'object' && !Object.keys(obj[curr]).length && checkObjIsDate(obj[curr])) {
               acc[curr] = obj[curr];
          }

          return acc;
     }, {});
};

export const convertToTitleCase = (str) => {
     return str.replace(/([A-Z])/g, ' $1').replace(/^./, function (match) {
          return match.toUpperCase();
     });
};

export const getElementRect = (element) => {
     if (!element) {
          return {
               bottom: 0,
               height: 0,
               left: 0,
               right: 0,
               top: 0,
               width: 0,
          };
     }

     return element.getBoundingClientRect();
};

export const updateNodeState = (nodes = [], nodeValue = '', checked = false) => {
     const updatedNodes = nodes.map((node) => {
          // If this is the node being changed
          if (node.value === nodeValue) {
               const updatedNode = { ...node, checked };

               // Update children if they exist
               if (node.children?.length) {
                    updatedNode.children = node.children.map((child) => ({
                         ...child,
                         checked,
                         indeterminate: false,
                    }));
               }

               return updatedNode;
          }

          // If this node has children, recursively update them
          if (node.children?.length) {
               const updatedChildren = updateNodeState(node.children, nodeValue, checked);

               // Calculate parent state based on children's state
               const allChecked = updatedChildren.every((child) => child.checked);
               const someChecked = updatedChildren.some((child) => child.checked);

               return {
                    ...node,
                    checked: allChecked,
                    indeterminate: !allChecked && someChecked,
                    children: updatedChildren,
               };
          }

          // Return unchanged node
          return node;
     });
     return updatedNodes;
};

export const convertFilterOptions = (options) => {
     return options.map((option) => ({
          ...option,
          checked: false,
          indeterminate: false,
          children: option.children?.length ? convertFilterOptions(option.children) : undefined,
     }));
};

export const pickPropertiesFromArrayObject = (array, properties = ['id']) => {
     if (!array.length || !properties.length) return [];

     return map(array, (item) => pick(item, properties));
};

export const pickPropertiesFromObject = (obj, properties = ['id']) => {
     if (!Object.keys(obj).length || !properties.length) return [];

     return pick(obj, properties);
};

export const getFullCertificationLevelName = (certLevel) => {
     const certLevelName = PREREQUISITE_CERTIFICATION_LEVELS.find((level) => level.value === certLevel)?.label || capitalizeFirstLetter(certLevel);
     return `${certLevelName}`;
};

export const checkIsCertLevelReached = (userLevel, targetLevel) => {
     const CERTIFICATION_LEVELS = [CERTIFICATION_LEVEL.TRAINED, CERTIFICATION_LEVEL.CERTIFICATED];

     //Check Invalid Input Level
     if (!CERTIFICATION_LEVELS.includes(userLevel) || !CERTIFICATION_LEVELS.includes(targetLevel)) return false;

     const userLevelValue = CERTIFICATION_LEVELS.findIndex((level) => level === userLevel);
     const targetLevelValue = CERTIFICATION_LEVELS.findIndex((level) => level === targetLevel);

     if (userLevelValue >= targetLevelValue) return true;

     return false;
};

export const newCheckPrerequisiteCompleted = ({ user, courses, product, products, eventId }) => {
     const timezoneSelected = localStorage.getItem(LOCAL_TIME_ZONE) || getTimeZoneGuess();
     const today = timezoneSelected && dayjs().tz(timezoneSelected);

     if (!product) return { status: false };

     const productId = product.id;
     const productData = products?.length ? products.find((item) => item?.id === productId) : null;

     if (!productData?.productPrerequisites?.length) {
          return { status: true };
     }

     for (let i = 0; i < productData.productPrerequisites.length; i++) {
          const certLevel = productData.productPrerequisites[i].certificationLevel;

          if (certLevel) {
               const isReach = checkIsCertLevelReached(user.certificateLevel, certLevel);
               const fullCertLevelName = getFullCertificationLevelName(certLevel);

               if (isReach) continue;

               return {
                    status: false,
                    prerequisiteProductName: '',
                    prerequisiteCertLevelName: fullCertLevelName,
               };
          }

          const prerequisiteId = productData.productPrerequisites[i].prerequisiteId;

          const prerequisiteProduct = prerequisiteId ? products.find((item) => item?.id === prerequisiteId) : null;

          //Check prerequisite of parent
          if (prerequisiteId && prerequisiteProduct) {
               for (let i = 0; i < prerequisiteProduct.productPrerequisites.length; i++) {
                    const certLevel = prerequisiteProduct.productPrerequisites[i].certificationLevel;

                    if (certLevel) {
                         const isReach = checkIsCertLevelReached(user.certificateLevel, certLevel);
                         const fullCertLevelName = getFullCertificationLevelName(certLevel);

                         if (isReach) continue;

                         return {
                              status: false,
                              prerequisiteProductName: '',
                              prerequisiteCertLevelName: fullCertLevelName,
                         };
                    }
               }
          }

          if (prerequisiteId && prerequisiteProduct && courses?.some((course) => course.productId === prerequisiteId)) {
               let prerequisiteCourse = courses.find((item) => item.productId === prerequisiteId);

               const course = courses.find((item) => item?.productId === productId);

               if (course?.status === COURSE_STATUS.COMPLETED && prerequisiteCourse) {
                    return { status: true };
               }

               if (eventId && courses.some((course) => course.productId === prerequisiteId && course.eventId === eventId)) {
                    prerequisiteCourse = courses.find((item) => item.productId === prerequisiteId && item.eventId === eventId);

                    if (prerequisiteCourse && prerequisiteCourse.status === COURSE_STATUS.COMPLETED) {
                         return {
                              status: true,
                              prerequisiteProductName: prerequisiteProduct.publishName,
                         };
                    }
               }

               const isParentPhysical = prerequisiteProduct.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT;
               const isParentEvent = prerequisiteProduct.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;

               const isParentPhysicalNotCompleted = isParentPhysical && prerequisiteCourse.lessonStatus !== COURSE_STATUS.COMPLETED;
               const isParentEventNotCompleted = isParentEvent && !prerequisiteCourse.confirmAttended;

               const isParentLessonsNotCompleted = prerequisiteProduct.lessons?.length && prerequisiteCourse.lessonStatus !== COURSE_STATUS.COMPLETED;
               const isParentSurveyNotCompleted = prerequisiteProduct.survey && prerequisiteCourse.surveyStatus !== COURSE_STATUS.COMPLETED;
               const isParentTestNotCompleted = prerequisiteProduct.test && prerequisiteCourse.testStatus !== COURSE_STATUS.COMPLETED;
               const isParentCePackNotCompleted =
                    prerequisiteProduct.test && prerequisiteProduct.cePack && prerequisiteCourse.cePackStatus !== COURSE_STATUS.COMPLETED;

               const parentExpireDay = dayjs(prerequisiteCourse?.seat?.expiredAt);
               const isParentExpired = today > parentExpireDay;

               const isCourseJustFilled = course?.status === COURSE_STATUS.FILLED;
               const isParentCourseCompleted = prerequisiteCourse.status === COURSE_STATUS.COMPLETED;

               if (
                    isParentPhysicalNotCompleted ||
                    isParentEventNotCompleted ||
                    isParentLessonsNotCompleted ||
                    isParentSurveyNotCompleted ||
                    isParentTestNotCompleted ||
                    isParentCePackNotCompleted ||
                    (isParentExpired && !isParentCourseCompleted && isCourseJustFilled)
               ) {
                    return {
                         status: false,
                         prerequisiteProductName: prerequisiteProduct.publishName,
                    };
               }
          } else {
               return {
                    status: false,
                    prerequisiteProductName: prerequisiteProduct?.publishName ?? '',
               };
          }
     }

     return { status: true };
};

export const checkObjIsDate = (dateStr) => {
     return !isNaN(new Date(dateStr));
};

export const checkingTestMode = (user) => {
     const isFastTest = Cookies.get(COOKIE_NAMES.FAST_TEST) === 'enabled';
     if (isFastTest && !user) return true;

     const userTestMode = !!user?.userTestMode;
     if (userTestMode) return true;

     return false;
};
